// ============================================================
// ByOurConsent.com -- Forum Thread Page
// /forum/:slug
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api   from '../utils/api';
import toast from 'react-hot-toast';

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

const Avatar = ({ name, photo, size = 40 }) => (
    photo ? (
        <img src={photo.startsWith('http') ? photo : `http://localhost:5002${photo}`}
            alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    ) : (
        <div style={{ width: size, height: size, borderRadius: '50%', background: '#1e2a3a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e8dfc8', fontSize: size * 0.4, fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 600, flexShrink: 0 }}>
            {name?.[0] || '?'}
        </div>
    )
);

const RoleBadge = ({ role }) => {
    if (!role || role === 'member') return null;
    const map = {
        superadmin:   { label: 'Admin',          bg: '#1e2a3a', color: '#e8dfc8' },
        admin:        { label: 'Admin',          bg: '#1e2a3a', color: '#e8dfc8' },
        group_leader: { label: 'Chapter leader', bg: '#8b6a3a', color: '#f5f0e8' },
        contributor:  { label: 'Contributor',    bg: '#3b6d11', color: '#f5f0e8' }
    };
    const s = map[role];
    if (!s) return null;
    return (
        <span style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', background: s.bg, color: s.color, padding: '2px 6px', borderRadius: '2px', fontWeight: 600 }}>
            {s.label}
        </span>
    );
};

const ForumThreadPage = () => {
    const { slug } = useParams();
    const { user, isAdmin } = useAuth();
    const replyRef = useRef(null);

    const [thread,  setThread]  = useState(null);
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reply,   setReply]   = useState('');
    const [posting, setPosting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/forum/threads/${slug}`);
            setThread(r.data.thread);
            setReplies(r.data.replies || []);
        } catch { toast.error('Thread not found'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [slug]);

    const postReply = async () => {
        if (!reply.trim()) { toast.error('Reply cannot be empty'); return; }
        setPosting(true);
        try {
            const r = await api.post(`/forum/threads/${thread.id}/reply`, { body: reply });
            setReplies(prev => [...prev, r.data.reply]);
            setReply('');
            toast.success('Reply posted!');
            // Scroll to new reply
            setTimeout(() => replyRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to post reply'); }
        finally { setPosting(false); }
    };

    const [confirmDelete, setConfirmDelete] = useState(null);

    const deleteReply = async (e, replyId) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await api.delete(`/forum/replies/${replyId}`);
            setReplies(prev => prev.filter(r => r.id !== replyId));
            setConfirmDelete(null);
            toast.success('Reply deleted');
        } catch { toast.error('Failed to delete'); }
    };

    const togglePin = async () => {
        try {
            await api.put(`/forum/threads/${thread.id}`, { is_pinned: !thread.is_pinned });
            setThread(p => ({ ...p, is_pinned: !p.is_pinned }));
            toast.success(thread.is_pinned ? 'Thread unpinned' : 'Thread pinned');
        } catch { toast.error('Failed'); }
    };

    const toggleLock = async () => {
        try {
            await api.put(`/forum/threads/${thread.id}`, { is_locked: !thread.is_locked });
            setThread(p => ({ ...p, is_locked: !p.is_locked }));
            toast.success(thread.is_locked ? 'Thread unlocked' : 'Thread locked');
        } catch { toast.error('Failed'); }
    };

    if (loading) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8' }}><Nav />
            <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
        </div>
    );

    if (!thread) return null;

    return (
        <>
            <Helmet>
                <title>{thread.title} — Forum — By Our Consent</title>
            </Helmet>

            <Nav />

            {/* Thread header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto' }}>
                    <Link to="/forum" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Forum
                    </Link>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        {thread.category_name && (
                            <span style={{ fontSize: '10px', background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: '20px', fontFamily: 'sans-serif', color: '#8a9aaa', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                {thread.category_name}
                            </span>
                        )}
                        {thread.is_pinned && <span style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#c8b87a' }}>📌 Pinned</span>}
                        {thread.is_locked && <span style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>🔒 Locked</span>}
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.15, marginBottom: '6px' }}>
                        {thread.title}
                    </h1>
                    <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                        {thread.reply_count} {thread.reply_count === 1 ? 'reply' : 'replies'} · {thread.view_count} views
                        {isAdmin && (
                            <span style={{ marginLeft: '12px' }}>
                                <button onClick={togglePin} style={{ background: 'none', border: 'none', color: '#8b9a7a', fontSize: '11px', fontFamily: 'sans-serif', cursor: 'pointer', textDecoration: 'underline', marginRight: '8px' }}>
                                    {thread.is_pinned ? 'Unpin' : 'Pin'}
                                </button>
                                <button onClick={toggleLock} style={{ background: 'none', border: 'none', color: '#8b9a7a', fontSize: '11px', fontFamily: 'sans-serif', cursor: 'pointer', textDecoration: 'underline' }}>
                                    {thread.is_locked ? 'Unlock' : 'Lock'}
                                </button>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ maxWidth: '760px', margin: '0 auto', padding: '1.5rem' }}>

                {/* Opening post */}
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderLeft: '3px solid #8b1a1a', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <Avatar name={thread.author_name} photo={thread.author_photo} size={44} />
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif' }}>
                                    {thread.author_name}
                                </span>
                                <RoleBadge role={thread.author_role} />
                                <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginLeft: 'auto' }}>
                                    {fmtDate(thread.created_at)}
                                </span>
                            </div>
                            {thread.body && (
                                <div style={{ fontSize: '15px', fontFamily: 'sans-serif', color: '#2a3040', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                    {thread.body}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Replies */}
                {replies.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>
                            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                        </div>
                        {replies.map((r, i) => (
                            <div key={r.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '1rem 0', borderBottom: '0.5px solid #c8beaa' }}>
                                <Avatar name={r.author_name} photo={r.author_photo} size={36} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif' }}>
                                            {r.author_name}
                                        </span>
                                        <RoleBadge role={r.author_role} />
                                        <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginLeft: 'auto' }}>
                                            {fmtDate(r.created_at)}
                                        </span>
                                        {(isAdmin || r.author_id === user?.id) && (
                                            confirmDelete === r.id ? (
                                                <span style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                                                    <span style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8b1a1a' }}>Delete?</span>
                                                    <button onClick={(e) => deleteReply(e, r.id)}
                                                        style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'2px 8px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                                        Yes
                                                    </button>
                                                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(null); }}
                                                        style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'2px 8px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px', color:'#4a5060' }}>
                                                        No
                                                    </button>
                                                </span>
                                            ) : (
                                                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(r.id); }}
                                                    style={{ background:'none', border:'none', color:'#c8beaa', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', textDecoration:'underline' }}>
                                                    Delete
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#2a3040', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                        {r.body}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={replyRef} />
                    </div>
                )}

                {/* Reply form */}
                {user && !thread.is_locked && (
                    <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '10px' }}>
                            Join the discussion
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                            <Avatar name={user.first_name} photo={user.avatar_url} size={36} />
                            <div style={{ flex: 1 }}>
                                <textarea
                                    value={reply}
                                    onChange={e => setReply(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) postReply(); }}
                                    placeholder="Write your reply... (Ctrl+Enter to post)"
                                    rows={4}
                                    style={{ width: '100%', background: '#fff', border: '0.5px solid #c8beaa', padding: '10px 12px', fontSize: '14px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' }}
                                />
                                <button onClick={postReply} disabled={posting || !reply.trim()}
                                    style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: posting ? 'default' : 'pointer', borderRadius: '3px', opacity: (posting || !reply.trim()) ? 0.6 : 1 }}>
                                    {posting ? 'Posting...' : 'Post reply →'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {thread.is_locked && (
                    <div style={{ padding: '1rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', textAlign: 'center' }}>
                        🔒 This discussion has been closed.
                    </div>
                )}

                {!user && (
                    <div style={{ padding: '1.25rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#4a5060', marginBottom: '10px' }}>
                            <Link to="/login" style={{ color: '#8b1a1a' }}>Log in</Link> to join the discussion
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default ForumThreadPage;
